/**
 * DotIllustration - Decorative dot pattern for mobile menu
 * 
 * Exported from Figma design system.
 * Used as a background decoration element.
 */

interface DotIllustrationProps {
  className?: string;
  /** Whether to hide from screen readers */
  ariaHidden?: boolean;
}

export function DotIllustration({ className = '', ariaHidden = true }: DotIllustrationProps) {
  // Calculate dot radius based on distance from center (vertical) and column (horizontal)
  const calculateDotRadius = (rowIndex: number, totalRows: number, colIndex: number): number => {
    const center = totalRows / 2;
    const distanceFromCenter = Math.abs(rowIndex - center);
    const normalizedDistance = distanceFromCenter / center; // 0 at center, 1 at edges
    
    // Base size: largest (1.2) at center, smallest (0.4) at edges
    const maxRadius = 1.2;
    const minRadius = 0.4;
    const baseRadius = maxRadius - (normalizedDistance * (maxRadius - minRadius));
    
    // Apply horizontal scaling - columns get smaller as they move away from left
    // Column 0 (leftmost) = 100%, Column 1 = 85%, Column 2 = 70%, Column 3 = 55%
    const horizontalScale = 1 - (colIndex * 0.15);
    
    return baseRadius * horizontalScale;
  };

  const totalRows = 42;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      viewBox="0 0 52 812"
      className={className}
      role="presentation"
      aria-hidden={ariaHidden}
    >
      <defs>
        <style>
          {`
            .dot-bg {
              fill: none;
            }
            .dot-primary {
              fill: #53389e;
              fill-rule: evenodd;
            }
            .dot-light {
              fill: #f9f5ff;
            }
          `}
        </style>
        <clipPath id="dot-clippath">
          <path 
            className="dot-bg" 
            d="M-442,0h88C-129.77,0,52,181.77,52,406s-181.77,406-406,406h-88c-224.23,0-406-181.77-406-406S-666.23,0-442,0Z"
          />
        </clipPath>
      </defs>
      <g clipPath="url(#dot-clippath)">
        {/* Dot pattern will be clipped to this shape */}
        <rect className="dot-light" x="0" y="0" width="52" height="812" />
        
        {/* Generate dot grid pattern with varying sizes */}
        {Array.from({ length: totalRows }).map((_, rowIndex) => {
          return (
            <g key={`row-${rowIndex}`}>
              {Array.from({ length: 4 }).map((_, colIndex) => {
                const dotRadius = calculateDotRadius(rowIndex, totalRows, colIndex);
                return (
                  <circle
                    key={`dot-${rowIndex}-${colIndex}`}
                    className="dot-primary"
                    cx={8 + colIndex * 12}
                    cy={10 + rowIndex * 19.5}
                    r={dotRadius}
                  />
                );
              })}
            </g>
          );
        })}
      </g>
    </svg>
  );
}
